import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Tabs, Tab,
  Skeleton, alpha, useTheme, Paper, Divider, Button,
  Tooltip, List, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Close, BadgeOutlined, History as HistoryIcon, BoltOutlined,
  Edit, SwapHoriz, ExitToApp, PersonAddAlt1, Phone, Home, Person,
  PhoneAndroid, Mail as MailIcon, GroupOutlined, DeleteForeverOutlined,
  Numbers, Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { Enrollment, SessionClass } from '../../types/sessions';
import { studentsApi } from '../../services/sessions/studentsApi';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';

import AcademicHistoryTimeline from './AcademicHistoryTimeline';
import StudentFeesTab from './StudentFeesTab';
import EditStudentIdentityDialog from './EditStudentIdentityDialog';
import PersonPhoto from '../people/PersonPhoto';
import EnrollExistingStudentDialog from './EnrollExistingStudentDialog';
import TransferClassDialog from '../enrollments/TransferClassDialog';
import ChangeRollNumberDialog from '../enrollments/ChangeRollNumberDialog';
import ExitEnrollmentDialog from '../enrollments/ExitEnrollmentDialog';
import StudentLettersTab from '../letters/StudentLettersTab';
import DeleteStudentDialog from './DeleteStudentDialog';

interface StudentProfileDrawerProps {
  open: boolean;
  studentId: string | null;
  classes: SessionClass[];      // pre-fetched for transfer dialog
  onClose: () => void;
  onMutated?: () => void;       // called after any state change so caller can refresh
}

type TabKey = 'identity' | 'history' | 'fees' | 'letters' | 'actions';

const StudentProfileDrawer: React.FC<StudentProfileDrawerProps> = ({
  open, studentId, classes, onClose, onMutated
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite, canRead } = useModulePermissions();
  const { activeSession, viewSession } = useSessionContext();
  const writable = canWrite('enrollments');
  // The Fees tab is gated at the drawer level: no plan → no tab, no
  // permission → no tab. Cheaper than rendering the empty state.
  const showFeesTab = canRead('fees');

  const [tab, setTab] = useState<TabKey>('identity');
  const [student, setStudent] = useState<any | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);

  // Dialogs
  const [editOpen, setEditOpen]         = useState(false);
  const [enrollOpen, setEnrollOpen]     = useState(false);
  const [transferEn, setTransferEn]     = useState<Enrollment | null>(null);
  const [changeRollEn, setChangeRollEn] = useState<Enrollment | null>(null);
  const [exitEn, setExitEn]             = useState<Enrollment | null>(null);
  const [deleteOpen, setDeleteOpen]     = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [s, es] = await Promise.all([
        studentsApi.get(studentId),
        enrollmentsApi.listForStudent(studentId)
      ]);
      setStudent(s);
      setEnrollments(es);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load student', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [studentId, enqueueSnackbar]);

  useEffect(() => {
    if (open && studentId) {
      setTab('identity');
      load();
    }
  }, [open, studentId, load]);

  const refresh = useCallback(async () => {
    await load();
    setTimelineKey(k => k + 1);
    onMutated?.();
  }, [load, onMutated]);

  // Context-aware: is there an "enrolled" row in the active session?
  const liveSessionId = activeSession?._id ?? viewSession?._id;
  const liveEnrollment = useMemo(() =>
    enrollments.find(e =>
      e.enrollmentStatus === 'enrolled' &&
      String(e.sessionId) === String(liveSessionId)
    ) ?? null,
    [enrollments, liveSessionId]
  );

  const hasAnyOpenEnrollment = enrollments.some(e => e.enrollmentStatus === 'enrolled');

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        // Full-height overlay with a dimming backdrop. Match the Schools
        // drawer's z-index (1250) so the paper renders above the fixed AppBar
        // (zIndex.drawer + 1 = 1201) instead of being clipped by it. Click on
        // the backdrop closes via the default MUI Modal onClose handler.
        ModalProps={{ keepMounted: false }}
        slotProps={{
          root: { style: { zIndex: 1250 } },
          paper: {
            style: { zIndex: 1250 },
            sx: {
              width: { xs: '100%', sm: 520 },
              display: 'flex',
              flexDirection: 'column'
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, pt: 2, pb: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Student
            </Typography>
            <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
          </Stack>

          {loading || !student ? (
            <Stack spacing={1}>
              <Skeleton variant="text" width={180} height={32} />
              <Skeleton variant="text" width={120} height={20} />
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <PersonPhoto
                name={student.name}
                caption={student.grNumber ? `GR ${student.grNumber}` : undefined}
                photoFilename={student.photoFilename}
                size={56}
                owner={writable && student.personId ? { student: String(student._id) } : undefined}
                onChanged={(photoFilename) => setStudent((prev: any) => (prev ? { ...prev, photoFilename } : prev))}
              />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.25 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                    {student.name}
                  </Typography>
                  {student.grNumber && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        color: 'text.disabled',
                        letterSpacing: 0.5,
                      }}
                    >
                      {student.grNumber}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Roll No {student.rollNumber || '—'}
                  {liveEnrollment && (
                    <>
                      {' · '}
                      <Typography component="span" variant="caption" color="success.main" fontWeight={600}>
                        Currently enrolled
                      </Typography>
                    </>
                  )}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid', borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 48, gap: 0.75 }
          }}
        >
          <Tab value="identity" iconPosition="start" icon={<BadgeOutlined fontSize="small" />} label="Identity" />
          <Tab value="history"  iconPosition="start" icon={<HistoryIcon  fontSize="small" />} label="History" />
          {showFeesTab && (
            <Tab value="fees" iconPosition="start" icon={<PaymentsIcon fontSize="small" />} label="Fees" />
          )}
          <Tab value="letters"  iconPosition="start" icon={<MailIcon     fontSize="small" />} label="Letters" />
          <Tab value="actions"  iconPosition="start" icon={<BoltOutlined fontSize="small" />} label="Actions" />
        </Tabs>

        {/* Body */}
        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
          {loading || !student ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={120} />
            </Stack>
          ) : (
            <>
              {tab === 'identity' && (
                <IdentityTab student={student} writable={writable} onEdit={() => setEditOpen(true)} />
              )}
              {tab === 'history' && (
                <AcademicHistoryTimeline studentId={String(student._id)} refreshKey={timelineKey} />
              )}
              {tab === 'fees' && showFeesTab && (
                <StudentFeesTab
                  studentId={String(student._id)}
                  refreshKey={timelineKey}
                  onMutated={() => setTimelineKey(k => k + 1)}
                />
              )}
              {tab === 'letters' && (
                <StudentLettersTab studentId={String(student._id)} studentName={student.name} />
              )}
              {tab === 'actions' && (
                <ActionsTab
                  writable={writable}
                  hasLiveEnrollment={Boolean(liveEnrollment)}
                  hasAnyOpenEnrollment={hasAnyOpenEnrollment}
                  onEdit={() => setEditOpen(true)}
                  onTransfer={() => liveEnrollment && setTransferEn(liveEnrollment)}
                  onChangeRoll={() => liveEnrollment && setChangeRollEn(liveEnrollment)}
                  onExit={() => liveEnrollment && setExitEn(liveEnrollment)}
                  onEnrollInAnother={() => setEnrollOpen(true)}
                  onDelete={() => setDeleteOpen(true)}
                />
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Dialogs */}
      <EditStudentIdentityDialog
        open={editOpen}
        student={student}
        onClose={() => setEditOpen(false)}
        onSaved={refresh}
      />
      <EnrollExistingStudentDialog
        open={enrollOpen}
        studentId={String(student?._id ?? '')}
        studentName={student?.name}
        onClose={() => setEnrollOpen(false)}
        onEnrolled={refresh}
      />
      <TransferClassDialog
        open={Boolean(transferEn)}
        enrollment={transferEn}
        classes={classes}
        onClose={() => setTransferEn(null)}
        onTransferred={refresh}
      />
      <ChangeRollNumberDialog
        open={Boolean(changeRollEn)}
        enrollment={changeRollEn}
        classes={classes}
        onClose={() => setChangeRollEn(null)}
        onChanged={refresh}
      />
      <ExitEnrollmentDialog
        open={Boolean(exitEn)}
        enrollment={exitEn}
        studentName={student?.name}
        onClose={() => setExitEn(null)}
        onExited={refresh}
      />
      <DeleteStudentDialog
        open={deleteOpen}
        studentId={student?._id ? String(student._id) : null}
        studentName={student?.name}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          // Student is gone — close everything and let the parent refresh
          // its list. The drawer can't show data that no longer exists.
          setDeleteOpen(false);
          onClose();
          onMutated?.();
        }}
        onCloseEnrollment={liveEnrollment ? () => {
          // Pivot from the blocked-delete dialog into the close-enrollment
          // flow so the user never gets stuck.
          setDeleteOpen(false);
          setExitEn(liveEnrollment);
        } : undefined}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Identity tab
// ─────────────────────────────────────────────────────────────────────────────

const IdentityField: React.FC<{ icon: React.ReactElement; label: string; value?: string }> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.75 }}>
    <Box sx={{ color: 'text.disabled', mt: 0.25 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
    </Box>
  </Stack>
);

const IdentityTab: React.FC<{ student: any; writable: boolean; onEdit: () => void }> = ({ student, writable, onEdit }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
        Personal details
      </Typography>
      {writable && (
        <Button size="small" startIcon={<Edit fontSize="small" />} onClick={onEdit}>
          Edit
        </Button>
      )}
    </Stack>
    <Divider sx={{ mb: 1 }} />
    {student.grNumber && (
      <IdentityField
        icon={<BadgeOutlined fontSize="small" />}
        label="GR Number"
        value={student.grNumber}
      />
    )}
    <IdentityField icon={<Person fontSize="small" />}        label="Full name"      value={student.name} />
    <IdentityField icon={<PhoneAndroid fontSize="small" />}  label="Student phone"  value={student.phone} />
    <IdentityField icon={<Person fontSize="small" />}        label="Father's name"  value={student.fatherName} />
    <IdentityField icon={<Phone fontSize="small" />}         label="Father's phone" value={student.fatherPhone} />
    <IdentityField icon={<Home fontSize="small" />}          label="Address"        value={student.address} />
    {student.contact && <IdentityField icon={<Phone fontSize="small" />} label="Additional contact" value={student.contact} />}
    {/* groupId may be a string (legacy) or a populated `{ _id, name }` from the
        student detail endpoint. Render the name when populated; fall back to
        a dim "Unassigned" tag otherwise. */}
    <IdentityField
      icon={<GroupOutlined fontSize="small" />}
      label="Group"
      value={
        student.groupId && typeof student.groupId === 'object'
          ? (student.groupId.name || '—')
          : student.groupId
            ? '—'
            : 'Unassigned'
      }
    />
  </Paper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Actions tab
// ─────────────────────────────────────────────────────────────────────────────

const ActionsTab: React.FC<{
  writable: boolean;
  hasLiveEnrollment: boolean;
  hasAnyOpenEnrollment: boolean;
  onEdit: () => void;
  onTransfer: () => void;
  onChangeRoll: () => void;
  onExit: () => void;
  onEnrollInAnother: () => void;
  onDelete: () => void;
}> = ({ writable, hasLiveEnrollment, hasAnyOpenEnrollment, onEdit, onTransfer, onChangeRoll, onExit, onEnrollInAnother, onDelete }) => {
  if (!writable) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          You don't have permission to modify this student.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
      <List dense disablePadding>
        <ActionItem
          icon={<PersonAddAlt1 fontSize="small" color="primary" />}
          primary="Enroll in another session"
          secondary={hasAnyOpenEnrollment
            ? "Pre-enroll for the next planning session, or re-enroll after a gap."
            : "Re-admit this student into a new session."}
          onClick={onEnrollInAnother}
        />
        <Divider component="li" />
        <ActionItem
          icon={<SwapHoriz fontSize="small" color="primary" />}
          primary="Transfer class or section"
          secondary={hasLiveEnrollment ? "Move within the current session — preserves audit." : "Available only for the current session's enrollment."}
          onClick={onTransfer}
          disabled={!hasLiveEnrollment}
        />
        <Divider component="li" />
        <ActionItem
          icon={<Numbers fontSize="small" color="primary" />}
          primary="Change roll number"
          secondary={hasLiveEnrollment ? "Reassign the roll in the current slot — no class or section change." : "Available only for the current session's enrollment."}
          onClick={onChangeRoll}
          disabled={!hasLiveEnrollment}
        />
        <Divider component="li" />
        <ActionItem
          icon={<ExitToApp fontSize="small" color="warning" />}
          primary="Close current enrollment"
          secondary={hasLiveEnrollment ? "Mark as transferred out or dropped out." : "No active enrollment to close."}
          onClick={onExit}
          disabled={!hasLiveEnrollment}
        />
        <Divider component="li" />
        <ActionItem
          icon={<Edit fontSize="small" color="action" />}
          primary="Edit identity"
          secondary="Update name, parents, contact — never touches enrollments."
          onClick={onEdit}
        />
        <Divider component="li" />
        <ActionItem
          icon={<DeleteForeverOutlined fontSize="small" color="error" />}
          primary="Delete student"
          secondary="Permanent — allowed only when no records (attendance / results / letters / fines) exist."
          onClick={onDelete}
          destructive
        />
      </List>
    </Paper>
  );
};

const ActionItem: React.FC<{
  icon: React.ReactElement;
  primary: string;
  secondary: string;
  onClick: () => void;
  disabled?: boolean;
  /** Tints the row label red and pulls a soft error wash on hover.
   *  Used for destructive actions (delete) so the visual weight matches
   *  the consequence. */
  destructive?: boolean;
}> = ({ icon, primary, secondary, onClick, disabled, destructive }) => {
  const theme = useTheme();
  return (
    <Tooltip title={disabled ? secondary : ''} placement="left">
      <Box>
        <ListItemButton
          onClick={onClick}
          disabled={disabled}
          sx={{
            py: 1.5, px: 2,
            ...(destructive && !disabled && {
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.06) },
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                fontWeight={500}
                sx={destructive ? { color: 'error.main' } : undefined}
              >
                {primary}
              </Typography>
            }
            secondary={<Typography variant="caption" color="text.secondary">{secondary}</Typography>}
          />
        </ListItemButton>
      </Box>
    </Tooltip>
  );
};

export default StudentProfileDrawer;
